import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LogbookAppState } from '../../logbook.reducer';
import { FormEntriesService } from '../../../shared/service/reports/form-entries/form-entries.service';
import * as ObjectActions from '../../reports/form-entries/form-entries.actions';
import * as AppActions from '../../app/actions';
import { catchError, of, switchMap } from 'rxjs';
import { ServiceUtilities } from '../../../shared/helper/service-utilities';
import { IGetManyResponse, IGetOneResponse } from '../../../shared/model/interface/crud-response-interface.model';
import { IFormSubmissionData } from '../../my-tasks/logs-form-entries/logs-form-entries.model';
import { ILogsFormEntriesColumn } from '../../../view/my-tasks/logs-form-entries/logs-form-entries.component.model';
import { IFormEntriesData } from './form-entries.model';
import { emptyAction } from '../../../../constants';
import { IFormSubmissionUserAction } from '../../../../constants.model';
import _ from 'lodash';
import { AdvancedFilterService } from '../../../shared/component/filter/advanced-filter/advanced-filter.service';
import { IGetFormEntriesAndUserDefinedFieldsData } from '../../page-configuration/page-configuration.model';
import { PageConfigurationService } from '../../page-configuration/page-configuration.service';
import { IActivityType } from '../../settings/activity-type/activity-type.model';
import { ActivityTypeService } from '../../settings/activity-type/activity-type.service';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class FormEntriesEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<LogbookAppState>,
    private readonly service: FormEntriesService,
    private readonly advancedFilterService: AdvancedFilterService,
    private readonly pageConfigurationService: PageConfigurationService,
    private readonly activityTypeService: ActivityTypeService,
  ) {}

  getReportData = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.START_FORM_ENTRIES_LOADING),
      switchMap((payload: ObjectActions.StartFormEntriesLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        let body: Record<string, unknown> = ServiceUtilities.prepareGenericBodyForRequest(
          _.omit(payload.tableQuery, ['customAdvancedFilter', 'mdAdvancedFilter']),
          false,
          Boolean(payload.tableQuery.formioFieldOrdering || payload.tableQuery.mdFieldOrdering),
        );

        if (!_.isEmpty(payload.tableQuery?.advancedFilter)) {
          const params: HttpParams = this.advancedFilterService.getSearchString(payload.tableQuery);

          params.keys().forEach((key: string): void => {
            _.set(body, key, params.get(key));
          });
        }

        if (!_.isEmpty(payload.tableQuery.customAdvancedFilter) && payload.tableQuery.customAdvancedFilter?.filters) {
          body = {
            ...body,
            advanced_filter: JSON.stringify(
              this.advancedFilterService.getCustomAdvancedFilterSearchParam(
                payload.tableQuery.customAdvancedFilter.filters,
              ),
            ),
          };
        }

        if (payload.tableQuery.mdAdvancedFilter?.filters?.length) {
          body = {
            ...body,
            md_advanced_filter: this.advancedFilterService.getMdAdvancedFilterSearchParam(
              payload.tableQuery.mdAdvancedFilter.filters,
            ),
          };
        }

        return this.service.getReportData(body).pipe(
          switchMap((response: IGetManyResponse<IFormEntriesData>) => {
            return of(
              new ObjectActions.FormEntriesLoaded(response),
              payload.dispatchHideLoader ? new AppActions.HideLoader() : new AppActions.EmptyAction(),
            );
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getFormEntriesRecord = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.START_FORM_ENTRIES_RECORD_LOADING),
      switchMap((payload: ObjectActions.StartFormEntriesRecordLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getFormEntriesRecord(payload.logbookId, payload.id).pipe(
          switchMap((response: IGetOneResponse<IFormSubmissionData>) => {
            return of(
              new ObjectActions.FormEntriesRecordLoaded(response.data),
              payload.dispatchLoader ? new AppActions.HideLoader() : new AppActions.EmptyAction(),
            );
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getFormEntriesHistory = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.START_FORM_ENTRIES_HISTORY_LOADING),
      switchMap((payload: ObjectActions.StartFormEntriesHistoryLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getFormEntriesHistory(payload.id).pipe(
          switchMap((response: IGetManyResponse<IFormSubmissionUserAction>) => {
            return of(
              new ObjectActions.FormEntriesHistoryLoaded(response),
              payload.dispatchLoader ? new AppActions.HideLoader() : new AppActions.EmptyAction(),
            );
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getFormEntriesColumns = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.START_FORM_ENTRIES_COLUMN_LOADING),
      switchMap((payload: ObjectActions.StartFormEntriesColumnLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getFormEntriesColumns(payload.formIds, payload.parentLogbookId).pipe(
          switchMap((response: IGetManyResponse<ILogsFormEntriesColumn>) => {
            return of(
              new ObjectActions.FormEntriesColumnLoaded(response),
              payload.dispatchHideLoader ? new AppActions.HideLoader() : new AppActions.EmptyAction(),
            );
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  downloadReportExcel = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.DOWNLOAD_REPORT_EXCEL),
      switchMap((objectData: ObjectActions.DownloadReportExcel) => {
        this.store.dispatch(new AppActions.ShowLoader());
        let body: Record<string, unknown> = ServiceUtilities.prepareGenericBodyForRequest(
          _.omit(objectData.tableQuery, ['customAdvancedFilter', 'mdAdvancedFilter']),
          false,
          Boolean(objectData.tableQuery.formioFieldOrdering || objectData.tableQuery.mdFieldOrdering),
        );

        if (!_.isEmpty(objectData.tableQuery?.advancedFilter)) {
          const params: HttpParams = this.advancedFilterService.getSearchString(objectData.tableQuery);

          params.keys().forEach((key: string): void => {
            _.set(body, key, params.get(key));
          });
        }

        if (
          !_.isEmpty(objectData.tableQuery.customAdvancedFilter) &&
          objectData.tableQuery.customAdvancedFilter?.filters
        ) {
          body = {
            ...body,
            advanced_filter: JSON.stringify(
              this.advancedFilterService.getCustomAdvancedFilterSearchParam(
                objectData.tableQuery.customAdvancedFilter.filters,
              ),
            ),
          };
        }

        if (objectData.tableQuery.mdAdvancedFilter?.filters?.length) {
          body = {
            ...body,
            md_advanced_filter: this.advancedFilterService.getMdAdvancedFilterSearchParam(
              objectData.tableQuery.mdAdvancedFilter.filters,
            ),
          };
        }

        this.service.downloadReportExcel(
          body,
          objectData.tableQuery,
          objectData.dataTableHeaders,
          objectData.rowFormioFields,
          objectData.lbkUserDefinedFields,
          objectData.frmUserDefinedFields,
          objectData.frmSelectableFields,
        );

        return emptyAction;
      }),
      catchError((error) => {
        return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  saveReportTableSettings = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.SAVE_REPORT_TABLE_SETTINGS),
      switchMap((objectData: ObjectActions.SaveReportTableSettings) => {
        return this.service.saveReportTableSettings(objectData.reportId, objectData.meta).pipe(
          switchMap(() => {
            return of(new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((error) => {
        return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  downloadReportExcelCompleted = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.DOWNLOAD_REPORT_EXCEL_COMPLETED),
      switchMap(() => {
        this.store.dispatch(new AppActions.HideLoader());
        return emptyAction;
      }),
      catchError((error) => {
        return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  getUserDefinedFieldsAndFormEntriesEffect = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.GET_USER_DEFINED_FIELDS_AND_FORM_ENTRIES),
      switchMap((payload: ObjectActions.GetUserDefinedFieldsAndFormEntries) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.pageConfigurationService.getFormEntriesAndUserDefinedFields(payload.payload).pipe(
          switchMap((response: IGetFormEntriesAndUserDefinedFieldsData) => {
            return of(
              new ObjectActions.GetUserDefinedFieldsAndFormEntriesCompleted(response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getActivityTypes = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.GET_ACTIVITY_TYPES),
      switchMap((payload: ObjectActions.GetActivityTypes) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.activityTypeService.listActivityTypes().pipe(
          switchMap((response: IGetManyResponse<IActivityType>) => {
            return of(new ObjectActions.GetActivityTypesCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );
}
